import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { PageResponse, Pagination } from '@core/shared/domain';
import { QueryParams } from '@core/shared/util';
import { DeletedFavoriteListArticle } from '@mpcm/shared';

import {
  CreateFavoritesList,
  FavoritesList,
  FavoritesListArticle,
  FavoritesListDeletedArticle,
  UpdateFavoritesList,
} from '../../common';

@Injectable()
export class FavoritesService {
  private readonly baseUrl = '/api/medical-shop2/favorites';

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches all favorites lists the current user has access to.
   * @returns All favorites lists
   */
  fetchFavoritesLists(): Observable<Array<FavoritesList>> {
    return this.http.get<Array<FavoritesList>>(`${this.baseUrl}`);
  }

  /**
   * Creates a new favorites list
   * @param createDto Contains the name of the new list and the added article
   * @returns The newly created list
   */
  createList(createDto: CreateFavoritesList): Observable<FavoritesList> {
    return this.http.post<FavoritesList>(`${this.baseUrl}`, createDto);
  }

  /**
   * Updates a favorites list
   * @param listId The ID of the updated list
   * @param updateDto The new values for the updated list
   * @returns The updated list
   */
  updateList(listId: string, updateDto: UpdateFavoritesList): Observable<FavoritesList> {
    return this.http.put<FavoritesList>(`${this.baseUrl}/${listId}`, {
      ...updateDto,
    });
  }

  /**
   * Deletes a favorites list
   * @param listId The ID of the list
   * @returns
   */
  deleteList(listId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${listId}`);
  }

  /**
   * Fetches all articles of a specific favorites list
   * @param listId The ID of the list
   * @param pagination The pagination settings
   * @returns A paged list of articles for the specific favorites list
   */
  getArticlesForList(
    listId: string,
    pagination: Partial<Pagination> = {},
  ): Observable<PageResponse<FavoritesListArticle>> {
    const { page = 1, pageSize = 50 } = pagination;
    const params = QueryParams.build<FavoritesListArticle>().page(page).pageSize(pageSize).toHttpParams();

    return this.http.get<PageResponse<FavoritesListArticle>>(`${this.baseUrl}/${listId}/articles`, {
      params,
    });
  }

  /**
   * Adds an article to a specific favorites list
   * @param listId The ID of the favorites list
   * @param globalArticleId The added article
   * @returns The updated favorites list
   */
  addArticleToList(listId: string, globalArticleId: string): Observable<FavoritesList> {
    return this.http.post<FavoritesList>(`${this.baseUrl}/${listId}/articles/${globalArticleId}`, {});
  }

  /**
   * Removes an article from a specific favorites list
   * @param listId The ID of the list
   * @param globalArticleId The ID of the removed article
   * @returns The updated favorites list
   */
  removeArticleFromList(listId: string, globalArticleId: string): Observable<FavoritesList> {
    return this.http.delete<FavoritesList>(`${this.baseUrl}/${listId}/articles/${globalArticleId}`);
  }

  /**
   * Fetches all deleted articles of a specific favorites list
   * @param listId The ID of the favorites list
   * @param pagination The pagination settings
   * @param searchTerm The search term for the deleted articles
   * @returns A list of deleted articles
   */
  fetchDeletedArticlesForList(
    listId: string,
    searchTerm?: string,
    pagination: Partial<Pagination> = {},
  ): Observable<PageResponse<FavoritesListDeletedArticle>> {
    const { page = 1, pageSize = 10 } = pagination;
    const params = QueryParams.build<DeletedFavoriteListArticle>()
      .page(page)
      .pageSize(pageSize)
      .param('searchTerm', searchTerm || undefined)
      .toHttpParams();

    return this.http.get<PageResponse<FavoritesListDeletedArticle>>(`${this.baseUrl}/${listId}/deletedarticles`, {
      params,
    });
  }

  /**
   * Removes one deleted article from a specific favorites list
   * @param listId The ID of the favorites list
   * @param globalArticleId The ID of the deleted article
   * @returns The updated favorites list
   */
  removeDeletedArticleFromList(listId: string, globalArticleId: string): Observable<FavoritesList> {
    return this.http.delete<FavoritesList>(`${this.baseUrl}/${listId}/deletedarticles/${globalArticleId}`);
  }

  /**
   * Removes all deleted articles from a specific favorites list
   * @param listId The ID of the favorites list
   * @param searchTerm The search term for the deleted articles
   * @returns The updated favorites list
   */
  removeAllDeletedArticlesFromList(listId: string, searchTerm?: string): Observable<FavoritesList> {
    const params = QueryParams.build<DeletedFavoriteListArticle>()
      .param('searchTerm', searchTerm || undefined)
      .toHttpParams();

    return this.http.delete<FavoritesList>(`${this.baseUrl}/${listId}/deletedarticles`, { params });
  }
}
